import { apiClient as api } from './api';
import {
  AnalyzeRssRequest,
  AnalyzeDelimitedDataRequest,
  DataAnalysisResponse,
  DataAnalysisSession,
  DataAnalysisResult,
  DataSummary,
  ColumnInfo
} from '../models/dataAnalysis';

const DATA_ANALYSIS_STORAGE_KEY = 'data_analysis_sessions';

// Helper functions
const generateSessionId = () => `data_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const saveSession = (session: DataAnalysisSession): void => {
  const sessions = JSON.parse(localStorage.getItem(DATA_ANALYSIS_STORAGE_KEY) || '{}');
  sessions[session.sessionId] = session;
  localStorage.setItem(DATA_ANALYSIS_STORAGE_KEY, JSON.stringify(sessions));
};

const getSession = (sessionId: string): DataAnalysisSession | null => {
  const sessions = JSON.parse(localStorage.getItem(DATA_ANALYSIS_STORAGE_KEY) || '{}');
  return sessions[sessionId] || null;
};

const getAllSessions = (): Record<string, DataAnalysisSession> => {
  return JSON.parse(localStorage.getItem(DATA_ANALYSIS_STORAGE_KEY) || '{}');
};

const deleteSession = (sessionId: string): void => {
  const sessions = JSON.parse(localStorage.getItem(DATA_ANALYSIS_STORAGE_KEY) || '{}');
  delete sessions[sessionId];
  localStorage.setItem(DATA_ANALYSIS_STORAGE_KEY, JSON.stringify(sessions));
};

// Data parsing helpers
const parseDelimitedData = (data: string, delimiter: string = ',', hasHeader: boolean = true): DataSummary => {
  const lines = data.trim().split('\n');
  if (lines.length === 0) {
    throw new Error('No data provided');
  }

  const headers = hasHeader ? lines[0].split(delimiter).map(h => h.trim()) : 
    lines[0].split(delimiter).map((_, i) => `Column ${i + 1}`);
  
  const rows = hasHeader ? lines.slice(1) : lines;
  const parsedData = rows.map(row => {
    const values = row.split(delimiter).map(v => v.trim());
    const rowObj: any = {};
    headers.forEach((header, index) => {
      rowObj[header] = values[index] || null;
    });
    return rowObj;
  });

  // Analyze columns
  const columns: ColumnInfo[] = headers.map(header => {
    const columnValues = parsedData.map(row => row[header]).filter(v => v !== null && v !== '');
    const numericValues = columnValues.filter(v => !isNaN(Number(v))).map(Number);
    const isNumeric = numericValues.length > columnValues.length * 0.8;

    const info: ColumnInfo = {
      name: header,
      type: isNumeric ? 'number' : 'string',
      uniqueValues: new Set(columnValues).size,
      nullCount: parsedData.length - columnValues.length
    };

    if (isNumeric && numericValues.length > 0) {
      info.minValue = Math.min(...numericValues);
      info.maxValue = Math.max(...numericValues);
      info.mean = numericValues.reduce((a, b) => a + b, 0) / numericValues.length;
      info.median = numericValues.sort((a, b) => a - b)[Math.floor(numericValues.length / 2)];
    }

    return info;
  });

  return {
    rowCount: parsedData.length,
    columnCount: headers.length,
    columns,
    sampleData: parsedData.slice(0, 10)
  };
};

export const dataAnalysisService = {
  // RSS Analysis
  async analyzeRss(request: AnalyzeRssRequest): Promise<DataAnalysisResponse> {
    const response = await api.post('/api/ingest/analyze-rss', request);
    return response.data;
  },

  // Delimited Data Analysis
  async analyzeDelimitedData(request: AnalyzeDelimitedDataRequest): Promise<DataAnalysisResponse> {
    const response = await api.post('/api/ingest/analyze-delimited-data', request);
    return response.data;
  },

  // Session Management
  createSession(title: string, analysisType: 'rss' | 'csv' | 'custom', sourceData?: string, sourceUrl?: string): DataAnalysisSession {
    const session: DataAnalysisSession = {
      sessionId: generateSessionId(),
      analysisType,
      title,
      sourceData,
      sourceUrl,
      analyses: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    saveSession(session);
    return session;
  },

  async performAnalysis(sessionId: string, prompt: string, temperature: number = 0.7): Promise<DataAnalysisResult> {
    const session = getSession(sessionId);
    if (!session) throw new Error('Session not found');

    let response: DataAnalysisResponse;
    
    if (session.analysisType === 'rss' && session.sourceUrl) {
      response = await this.analyzeRss({
        rssUrl: session.sourceUrl,
        userPrompt: prompt,
        temperature
      });
    } else if (session.analysisType === 'csv' && session.sourceData) {
      response = await this.analyzeDelimitedData({
        delimitedData: session.sourceData,
        userPrompt: prompt,
        delimiter: ',',
        hasHeaderRow: true,
        temperature
      });
    } else {
      throw new Error('Invalid session configuration');
    }

    const result: DataAnalysisResult = {
      id: `analysis_${Date.now()}`,
      prompt,
      analysis: response.analysis,
      insights: response.insights || [],
      recommendations: response.recommendations,
      timestamp: new Date().toISOString(),
      temperature
    };

    session.analyses.push(result);
    session.updatedAt = new Date().toISOString();
    saveSession(session);

    return result;
  },

  // Data Processing
  parseDelimitedData,

  // Export functions
  exportSessionAsMarkdown(sessionId: string): string {
    const session = getSession(sessionId);
    if (!session) throw new Error('Session not found');

    let markdown = `# ${session.title}\n\n`;
    markdown += `**Created:** ${new Date(session.createdAt).toLocaleString()}\n`;
    markdown += `**Type:** ${session.analysisType.toUpperCase()}\n\n`;

    if (session.sourceUrl) {
      markdown += `**Source:** ${session.sourceUrl}\n\n`;
    }

    markdown += `## Analyses\n\n`;

    session.analyses.forEach((analysis, index) => {
      markdown += `### Analysis ${index + 1}\n`;
      markdown += `**Prompt:** ${analysis.prompt}\n\n`;
      markdown += `**Analysis:**\n${analysis.analysis}\n\n`;
      
      if (analysis.insights.length > 0) {
        markdown += `**Key Insights:**\n`;
        analysis.insights.forEach(insight => {
          markdown += `- ${insight}\n`;
        });
        markdown += '\n';
      }

      if (analysis.recommendations && analysis.recommendations.length > 0) {
        markdown += `**Recommendations:**\n`;
        analysis.recommendations.forEach(rec => {
          markdown += `- ${rec}\n`;
        });
        markdown += '\n';
      }

      markdown += `---\n\n`;
    });

    return markdown;
  },

  exportSessionAsJSON(sessionId: string): string {
    const session = getSession(sessionId);
    if (!session) throw new Error('Session not found');
    return JSON.stringify(session, null, 2);
  },

  // Local storage management
  getSession,
  getAllSessions,
  deleteSession,
  getAllSessionIds: () => Object.keys(getAllSessions()),
  
  getSessionsByType: (analysisType: string) => {
    const sessions = getAllSessions();
    return Object.values(sessions).filter(s => s.analysisType === analysisType);
  }
};